import { collection, addDoc, getDocs, query, where, doc, updateDoc, deleteDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { updateAppointmentsAggregationOnCreate, updateAppointmentsAggregationOnDelete } from './appointmentsAggregations';

// Função para calcular a próxima data de um dia da semana
const getNextDateForDay = (dayOfWeek) => {
  const today = new Date();
  const daysOfWeek = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const targetDayIndex = daysOfWeek.indexOf(dayOfWeek);
  
  const currentDayIndex = today.getDay();
  let daysUntilTarget = targetDayIndex - currentDayIndex;
  
  // Se o dia já passou esta semana, buscar na próxima
  if (daysUntilTarget <= 0) {
    daysUntilTarget += 7;
  }
  
  const nextDate = new Date(today);
  nextDate.setDate(today.getDate() + daysUntilTarget);
  nextDate.setHours(0, 0, 0, 0);
  
  return nextDate;
};

// Função para gerar agendamentos mensais
export const generateMonthlyAppointments = async (pet) => {
  if (!pet.planoMensal || !pet.diasAgendados || pet.diasAgendados.length === 0) {
    return;
  }

  try {
    // Verificar se já existem agendamentos para este mês
    const appointmentsQuery = query(
      collection(db, 'agendamentos'),
      where('idPet', '==', pet.id)
    );
    
    const existingAppointments = await getDocs(appointmentsQuery);
    const existingDates = new Set();
    
    existingAppointments.docs.forEach(doc => {
      const data = doc.data();
      // Filtrar apenas agendamentos recorrentes
      if (data.eRecorrente && data.dataAgendamento) {
        const date = data.dataAgendamento.toDate ? data.dataAgendamento.toDate() : new Date(data.dataAgendamento);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        // Criar chave única considerando data e hora
        existingDates.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${hours}-${minutes}`);
      }
    });

    const appointmentsToCreate = [];
    const createdAppointmentsDates = []; // Array para armazenar as datas dos agendamentos criados
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Criar agendamentos usando as datas fornecidas pelo usuário
    for (const scheduledDay of pet.diasAgendados) {
      // Verificar se temos data e hora válidas
      if (!scheduledDay.date || !scheduledDay.time) {
        continue;
      }
      
      // Extrair horas e minutos do horário primeiro
      const [hours, minutes] = scheduledDay.time.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) {
        continue; // Horário inválido
      }
      
      // Converter a data string para objeto Date
      // Criar data a partir da string no formato YYYY-MM-DD
      const dateParts = scheduledDay.date.split('-');
      const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      date.setHours(0, 0, 0, 0); // Normalizar para meia-noite para comparação
      
      if (isNaN(date.getTime())) {
        continue; // Data inválida
      }
      
      // Verificar se a data já passou (comparar apenas a data, não a hora)
      // Usar <= para permitir agendamentos para hoje
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      if (dateOnly < todayOnly) {
        continue;
      }
      
      // Criar chave única considerando data e hora para evitar duplicatas
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${hours}-${minutes}`;
      if (existingDates.has(dateKey)) {
        continue;
      }
      
      const appointmentDate = new Date(date);
      appointmentDate.setHours(hours, minutes, 0, 0);
      
      // Usar o tipo de serviço configurado no pet, ou padrão se não estiver definido
      const tipoServicoLabel = pet.tipoServicoPlanoMensal || pet.planoMensalServiceType || 'Banho + Tosa (Plano Mensal)';
      const recurringInterval = pet.intervaloRecorrente || 'weekly';
      
      appointmentsToCreate.push({
        idPet: pet.id,
        nomePet: pet.nome,
        ownernome: pet.tutor || '',
        telefone: pet.telefone || '',
        tipoServico: tipoServicoLabel.toUpperCase(),
        dataAgendamento: Timestamp.fromDate(appointmentDate),
        precoServico: 0, // Será preenchido abaixo (preço do plano / nº de agendamentos)
        observacoes: `Agendamento do plano mensal (${recurringInterval === 'biweekly' ? 'a cada 2 semanas' : 'semanal'})`.toUpperCase(),
        status: 'agendado',
        eRecorrente: true,
        origemRecorrente: 'monthly_plan',
        intervaloRecorrente: recurringInterval,
        criadoEm: Timestamp.now()
      });
      
      // Armazenar a data formatada do agendamento
      createdAppointmentsDates.push({
        date: appointmentDate,
        time: scheduledDay.time
      });
    }
    
    // Dividir o preço do plano pelo número de agendamentos e gravar em precoServico
    const precoPlano = parseFloat(pet.precoPlanoMensal ?? pet.planoMensalPrice ?? 0) || 0;
    const qtdAgendamentos = appointmentsToCreate.length;
    const precoPorAgendamento = qtdAgendamentos > 0
      ? Math.round((precoPlano / qtdAgendamentos) * 100) / 100
      : 0;
    for (const apt of appointmentsToCreate) {
      apt.precoServico = precoPorAgendamento;
    }
    
    // Criar todos os agendamentos
    const successfullyCreatedDates = [];
    for (let i = 0; i < appointmentsToCreate.length; i++) {
      const appointmentData = appointmentsToCreate[i];
      try {
        const appointmentDocRef = await addDoc(collection(db, 'agendamentos'), appointmentData);
        
        // Buscar o agendamento criado do Firestore
        const newAppointmentDoc = await getDoc(doc(db, 'agendamentos', appointmentDocRef.id));
        if (newAppointmentDoc.exists()) {
          const newAppointmentData = { id: newAppointmentDoc.id, ...newAppointmentDoc.data() };
          
          // Adicionar a data do agendamento criado com sucesso
          if (i < createdAppointmentsDates.length) {
            successfullyCreatedDates.push(createdAppointmentsDates[i]);
          }
          
          // Atualizar agregado de agendamentos
          try {
            await updateAppointmentsAggregationOnCreate(newAppointmentData);
          } catch (aggError) {
            // Erro ao atualizar agregado de agendamentos
            // Continuar mesmo se a atualização do agregado falhar
          }
        }
      } catch (error) {
        // Erro ao criar agendamento automático
        // Continuar criando os outros agendamentos mesmo se um falhar
      }
    }
    
    // Retornar as datas dos agendamentos criados com sucesso
    return successfullyCreatedDates;
    
  } catch (error) {
    // Erro ao gerar agendamentos recorrentes
    throw error;
  }
};

// Função para atualizar agendamentos quando o plano é alterado
export const updateRecurringAppointments = async (pet) => {
  try {
    // Buscar todos os agendamentos recorrentes do pet
    const appointmentsQuery = query(
      collection(db, 'agendamentos'),
      where('idPet', '==', pet.id)
    );
    
    const appointmentsSnapshot = await getDocs(appointmentsQuery);
    
    // Deletar todos os agendamentos futuros recorrentes
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (const appointmentDoc of appointmentsSnapshot.docs) {
      const appointment = appointmentDoc.data();
      // Filtrar apenas agendamentos recorrentes
      if (appointment.eRecorrente && appointment.dataAgendamento) {
        const appointmentDate = appointment.dataAgendamento.toDate ? appointment.dataAgendamento.toDate() : new Date(appointment.dataAgendamento);
        
        if (appointmentDate >= today) {
          await deleteDoc(doc(db, 'agendamentos', appointmentDoc.id));
          
          // Atualizar agregado de agendamentos
          try {
            await updateAppointmentsAggregationOnDelete(appointmentDoc.id, appointment.dataAgendamento);
          } catch (aggError) {
            // Erro ao atualizar agregado de agendamentos na exclusão
            // Continuar mesmo se a atualização do agregado falhar
          }
        }
      }
    }
    
    // Gerar novos agendamentos com base nos novos horários
    if (pet.planoMensal && pet.diasAgendados && pet.diasAgendados.length > 0) {
      const createdDates = await generateMonthlyAppointments(pet);
      return createdDates || [];
    }
    
    return [];
  } catch (error) {
    // Erro ao atualizar agendamentos recorrentes
    throw error;
  }
};

